import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Link } from "react-router-dom";
import { COLORS } from "../../lib/brand";
import { ClipboardList, CheckSquare, DollarSign, Users, ArrowRight, AlertTriangle } from "lucide-react";

const STATUS_LABEL = {
  ready_for_processing: "Ready",
  in_processing: "In Processing",
  embassy_legalization: "Embassy",
  complete: "Complete",
  shipped: "Shipped",
};

export default function AdminDashboard() {
  const [orders, setOrders] = useState([]);
  const [tasks, setTasks] = useState({ tasks: [], date: "" });
  const [notaries, setNotaries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/apostille/orders"),
      api.get("/admin/tasks"),
      api.get("/admin/notaries"),
    ])
      .then(([o, t, n]) => {
        setOrders(o.data || []);
        setTasks(t.data || { tasks: [] });
        setNotaries(n.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openOrders = orders.filter((o) => !["complete", "shipped"].includes(o.status)).length;
  const completedTasks = tasks.tasks.filter((t) => t.checked).length;
  const totalTasks = tasks.tasks.length;
  const activeNotaries = notaries.filter((n) => n.status === "active").length;
  const notariesMissingDocs = notaries.filter((n) => (n.missing_credentials || []).length > 0).length;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <header>
        <h1 className="text-3xl font-semibold text-[#1A1A1A]">Welcome back</h1>
        <p className="text-sm text-[#5C5C5C] mt-1">Here's what's on deck — {tasks.date}</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard icon={<ClipboardList />} label="Open Apostille Orders" value={openOrders} link="/admin/orders" />
        <StatCard icon={<Users />} label="Active Notaries" value={activeNotaries} sub={notariesMissingDocs > 0 ? `${notariesMissingDocs} missing docs` : "all complete"} subWarning={notariesMissingDocs > 0} link="/admin/notaries" />
        <StatCard icon={<CheckSquare />} label="Today's Tasks" value={`${completedTasks} / ${totalTasks}`} link="/admin/tasks" />
        <StatCard icon={<DollarSign />} label="Finance & Receipts" value="Open" link="/admin/finance" />
      </div>

      <section className="bg-white rounded-xl border border-[#E8E2D5] shadow-sm">
        <div className="px-6 py-4 border-b border-[#E8E2D5] flex items-center justify-between">
          <h2 className="text-lg font-medium text-[#1A1A1A]">Latest orders</h2>
          <Link to="/admin/orders" className="text-sm text-[#800000] hover:text-[#5C0000] flex items-center gap-1" data-testid="dashboard-view-all-orders">
            View all <ArrowRight size={14} />
          </Link>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-[#5C5C5C]">Loading...</div>
        ) : orders.length === 0 ? (
          <div className="p-6 text-sm text-[#5C5C5C]">No orders yet.</div>
        ) : (
          <ul className="divide-y divide-[#E8E2D5]">
            {orders.slice(0, 5).map((o) => (
              <li key={o.id} className="px-6 py-4 flex items-center justify-between">
                <div>
                  <div className="font-medium text-[#1A1A1A]">{o.first_name} {o.last_name}</div>
                  <div className="text-xs text-[#5C5C5C]">{o.destination_country} · {o.document_count} doc(s)</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs px-2.5 py-1 rounded-full" style={{ background: COLORS.cream, color: COLORS.maroon }}>
                    {STATUS_LABEL[o.status] || o.status}
                  </span>
                  <Link to={`/admin/orders/${o.id}`} className="text-sm text-[#800000] hover:text-[#5C0000]">Open</Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function StatCard({ icon, label, value, sub, subWarning, link }) {
  return (
    <Link to={link} className="bg-white rounded-xl border border-[#E8E2D5] p-5 shadow-sm hover:shadow-md transition flex items-center gap-4" data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="w-11 h-11 rounded-lg flex items-center justify-center text-[#800000]" style={{ background: COLORS.cream }}>
        {icon}
      </div>
      <div>
        <div className="text-xs uppercase tracking-wide text-[#5C5C5C]">{label}</div>
        <div className="text-2xl font-semibold text-[#1A1A1A] mt-0.5">{value}</div>
        {sub && (
          <div className={`text-[10px] uppercase tracking-wider mt-1 flex items-center gap-1 ${subWarning ? "text-[#B8941F]" : "text-[#5C5C5C]"}`}>
            {subWarning && <AlertTriangle size={10} />} {sub}
          </div>
        )}
      </div>
    </Link>
  );
}
